import { PageShell } from "@/components/layout/PageShell";
import { canAccessPriority } from "@/components/priorities/priority-access";
import { buildBlockedNote } from "@/components/priorities/buildBlockedNote";
import { PriorityCommentSection } from "@/components/priorities/PriorityCommentSection";
import { PriorityForm } from "@/components/priorities/PriorityForm";
import { ClaimCandidateButton } from "@/components/priorities/ClaimCandidateButton";
import { PriorityReportsList } from "@/components/priorities/PriorityReportsList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { priorityFormKey, submitLabels } from "@/components/priorities/priorityUtils";
import type { SubmitIntent } from "@/components/priorities/form/priority-form-constants";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { usePriorityEditPermission } from "@/hooks/usePriorityEditPermission";
import { cn } from "@/lib/utils";
import { useDeletePriority } from "@/hooks/useDeletePriority";
import { priorityService } from "@/services/priorityService";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  FileText,
  Loader2,
  Lock,
  MessageSquare,
  Trash2,
} from "lucide-react";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

function getErrorStatus(error: unknown) {
  if (error && typeof error === "object" && "status" in error) {
    return (error as { status?: number }).status;
  }
  return undefined;
}

function LoadingState() {
  return (
    <>
      <Skeleton className="mb-6 h-9 w-32" />
      <Skeleton className="mb-4 h-32 w-full rounded-xl" />
      <Skeleton className="h-96 w-full rounded-xl" />
    </>
  );
}

interface MessageStateProps {
  title: string;
  description: string;
  action?: ReactNode;
}


function MessageState({ title, description, action }: MessageStateProps) {
  return (
    <>
      <Button asChild variant="ghost" className="mb-6 gap-2 px-0">
        <Link to="/priorities">
          <ArrowLeft className="h-4 w-4" />
          Back to Priorities
        </Link>
      </Button>
      <div className="rounded-xl border border-dashed border-border bg-muted/40 p-12 text-center">
        <AlertCircle className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
        {action ? <div className="mt-6">{action}</div> : null}
      </div>
    </>
  );
}

const PriorityDetail = () => {
  const { priorityPid } = useParams<{ priorityPid: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAdmin, canViewOkr } = useAuth();

  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [reportCount, setReportCount] = useState(0);
  const formSubmitRef = useRef<((intent?: SubmitIntent) => void) | null>(null);

  const {
    data: priority,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["priorities", priorityPid],
    queryFn: () => priorityService.getPriority(priorityPid as string),
    enabled: !!priorityPid,
  });
  // Same gating the embedded form uses, so the footer says what saving will actually do.
  const { canEditTask, canEditOwnRow } = usePriorityEditPermission(priority);

  const deleteMutation = useDeletePriority();
  const handleDelete = (pid: string) =>
    deleteMutation.mutate(pid, {
      onSuccess: () => {
        toast({ title: "Priority deleted", description: "The priority has been permanently deleted." });
        navigate("/priorities");
      },
      onError: () => toast({ title: "Error", description: "Failed to delete priority. Please try again.", variant: "destructive" }),
    });

  const handleCommentCountChange = useCallback((count: number) => setCommentCount(count), []);
  const handleReportCountChange = useCallback((count: number) => setReportCount(count), []);

  if (!priorityPid) {
    return <PageShell><MessageState title="Priority not found" description="The priority link is missing an identifier." /></PageShell>;
  }

  if (isLoading) {
    return <PageShell><LoadingState /></PageShell>;
  }

  if (isError || !priority) {
    const status = getErrorStatus(error);
    const isNotFound = status === 404;
    const isForbidden = status === 403;
    return (
      <PageShell>
        <MessageState
          title={isNotFound ? "Priority not found" : isForbidden ? "This deliverable is confidential" : "Could not load priority"}
          description={
            isNotFound
              ? "This priority may have been deleted or moved."
              : isForbidden
              ? "Only leaders, its creator and its assignees can view it."
              : "There was a problem loading this priority."
          }
          action={
            !isNotFound && !isForbidden ? (
              <Button onClick={() => refetch()} className="gap-2" disabled={isFetching}>
                <Loader2 className={cn("h-4 w-4", isFetching && "animate-spin")} />
                Try Again
              </Button>
            ) : null
          }
        />
      </PageShell>
    );
  }

  if (!canAccessPriority(priority, user?.pid, canViewOkr)) {
    return <PageShell><MessageState title="This deliverable is confidential" description="Only leaders, its creator and its assignees can view it." /></PageShell>;
  }

  const blockedNote = buildBlockedNote(priority);
  const canDelete = isAdmin || priority.isCreator;
  const labels = submitLabels(priority, !canEditTask && canEditOwnRow);
  const secondary = labels.secondary;

  return (
    <PageShell>
      {/* Top bar: back link + priority identity + actions */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button asChild variant="ghost" className="shrink-0 gap-2 px-0">
            <Link to="/priorities">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <span className="text-muted-foreground/40">/</span>
          <div className="flex min-w-0 items-center gap-2">
            <Badge variant="outline" className="shrink-0 font-mono text-xs">
              {priority.code}
            </Badge>
            {priority.isCandidate && (
              <Badge variant="outline" className="shrink-0 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                Candidate
              </Badge>
            )}
            <ClaimCandidateButton priority={priority} className="h-7 text-xs" />
            {(priority.isConfidential || priority.inheritsConfidential) && <span title="Confidential"><Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" /></span>}
            <span className="truncate text-sm font-medium text-foreground/80">{priority.name}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canDelete ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setIsDeleteAlertOpen(true)}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          ) : null}
          {secondary ? (
            <Button size="sm" variant="secondary" onClick={() => formSubmitRef.current?.(secondary.intent)} disabled={isFormSubmitting}>
              {secondary.label}
            </Button>
          ) : null}
          <Button
            size="sm"
            className="gap-2"
            onClick={() => formSubmitRef.current?.(labels.primary.intent)}
            disabled={isFormSubmitting}
          >
            {isFormSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {labels.primary.label}
          </Button>
        </div>
      </div>

      {/* Blocked note */}
      {blockedNote ? (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{blockedNote}</p>
        </div>
      ) : null}

      {/* Edit form section */}
      <div className="mb-5 overflow-hidden rounded-xl border border-border bg-card">
        <div className="px-4 py-0">
          <PriorityForm
            key={priorityFormKey(priority)}
            initialData={priority}
            hideButtons={true}
            onSubmitRef={(submitFn) => { formSubmitRef.current = submitFn; }}
            onSubmittingChange={setIsFormSubmitting}
            onSuccess={() => {
              setIsFormSubmitting(false);
            }}
            onCancel={() => navigate("/priorities")}
          />
        </div>
      </div>

      {/* Activity — Comments + Reports */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Tabs defaultValue="comments">
          <div className="flex flex-col gap-3 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold">Activity</h2>
            <TabsList className="grid h-9 w-full grid-cols-2 sm:w-auto">
              <TabsTrigger value="comments" className="gap-2">
                <MessageSquare className="h-3.5 w-3.5" />
                Comments
                {commentCount > 0 ? (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {commentCount}
                  </Badge>
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="reports" className="gap-2">
                <FileText className="h-3.5 w-3.5" />
                Reports
                {reportCount > 0 ? (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {reportCount}
                  </Badge>
                ) : null}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="comments" className="m-0 p-6">
            <PriorityCommentSection
              priorityPid={priority.pid}
              enabled={true}
              onCountChange={handleCommentCountChange}
            />
          </TabsContent>
          <TabsContent value="reports" className="m-0 min-h-[240px] p-6">
            <PriorityReportsList
              priorityPid={priority.pid}
              enabled={true}
              onCountChange={handleReportCountChange}
            />
          </TabsContent>
        </Tabs>
      </div>


      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete priority?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-semibold text-foreground">{priority.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(priority.pid)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
};

export default PriorityDetail;
